import { Inject, Injectable, Optional, Provider } from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
} from '@angular/material/core';
import { Dayjs } from 'dayjs';
import dayjs from './dayjs.setup';

/**
 * Display/parse formats for Material datepickers, mirroring the tokens the
 * previous MomentDateAdapter used so picker input/label output is unchanged.
 */
export const DAYJS_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const range = (length: number, valueFn: (index: number) => string): string[] =>
  Array.from({ length }, (_, i) => valueFn(i));

/**
 * In-repo Day.js implementation of Angular Material's `DateAdapter`. Replaces
 * `@angular/material-moment-adapter` so datepickers emit `Dayjs` values instead
 * of `Moment`, letting Moment be dropped from the bundle. Behaviour is kept
 * equivalent to the Moment adapter (non-strict `parse`, overflow-checked
 * `createDate`, locale-aware names).
 */
@Injectable()
export class DayjsDateAdapter extends DateAdapter<Dayjs> {
  constructor(@Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string) {
    super();
    this.setLocale(dateLocale || 'en');
  }

  private createDayjs(...args: any[]): Dayjs {
    return dayjs(...args).locale(this.locale);
  }

  private get localeData() {
    return dayjs().locale(this.locale).localeData();
  }

  getYear(date: Dayjs): number {
    return date.year();
  }

  getMonth(date: Dayjs): number {
    return date.month();
  }

  getDate(date: Dayjs): number {
    return date.date();
  }

  getDayOfWeek(date: Dayjs): number {
    return date.day();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const data = this.localeData;
    return style === 'long' ? data.months() : data.monthsShort();
  }

  getDateNames(): string[] {
    return range(31, (i) => String(i + 1));
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const data = this.localeData;
    if (style === 'long') {
      return data.weekdays();
    }
    return style === 'short' ? data.weekdaysShort() : data.weekdaysMin();
  }

  getYearName(date: Dayjs): string {
    return date.format('YYYY');
  }

  getFirstDayOfWeek(): number {
    return this.localeData.firstDayOfWeek();
  }

  getNumDaysInMonth(date: Dayjs): number {
    return date.daysInMonth();
  }

  clone(date: Dayjs): Dayjs {
    return date.clone();
  }

  createDate(year: number, month: number, date: number): Dayjs {
    if (month < 0 || month > 11) {
      throw Error(
        `Invalid month index "${month}". Month has to be between 0 and 11.`
      );
    }
    if (date < 1) {
      throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
    }
    const result = this.createDayjs(new Date(year, month, date));
    // Reject overflow (e.g. Feb 31 rolling into March), matching MomentDateAdapter.
    if (result.month() !== month) {
      throw Error(`Invalid date "${date}" for month with index "${month}".`);
    }
    if (!result.isValid()) {
      throw Error(`Invalid date "${date}" for month with index "${month}".`);
    }
    return result;
  }

  today(): Dayjs {
    return this.createDayjs();
  }

  parse(value: any, parseFormat: string | string[]): Dayjs | null {
    if (value && typeof value === 'string') {
      // Non-strict parse, matching the previous MomentDateAdapter behaviour.
      return this.createDayjs(value, parseFormat, this.locale, false);
    }
    return value ? this.createDayjs(value) : null;
  }

  format(date: Dayjs, displayFormat: string): string {
    if (!this.isValid(date)) {
      throw Error('DayjsDateAdapter: Cannot format invalid date.');
    }
    return date.locale(this.locale).format(displayFormat);
  }

  addCalendarYears(date: Dayjs, years: number): Dayjs {
    return date.add(years, 'year');
  }

  addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return date.add(months, 'month');
  }

  addCalendarDays(date: Dayjs, days: number): Dayjs {
    return date.add(days, 'day');
  }

  toIso8601(date: Dayjs): string {
    return date.toISOString();
  }

  override deserialize(value: any): Dayjs | null {
    let date: Dayjs | undefined;
    if (value instanceof Date) {
      date = this.createDayjs(value);
    } else if (this.isDateInstance(value)) {
      return this.clone(value);
    } else if (typeof value === 'string') {
      if (!value) {
        return null;
      }
      // ISO 8601 strings, matching the base adapter contract.
      date = this.createDayjs(value);
    }
    if (date && this.isValid(date)) {
      return date;
    }
    return super.deserialize(value);
  }

  isDateInstance(obj: any): boolean {
    return dayjs.isDayjs(obj);
  }

  isValid(date: Dayjs): boolean {
    return date.isValid();
  }

  invalid(): Dayjs {
    return this.createDayjs(null);
  }
}

/**
 * Drop-in replacement for `provideMomentDateAdapter()`. Registers the Day.js
 * adapter plus the given (or default) Material date formats.
 */
export function provideDayjsDateAdapter(
  formats: MatDateFormats = DAYJS_DATE_FORMATS
): Provider[] {
  return [
    {
      provide: DateAdapter,
      useClass: DayjsDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: formats },
  ];
}
